import { useCallback, useEffect, useRef, useState } from "react";
import {
  useAgent,
  type AgentState,
  useConnectionState,
  useDataChannel,
  useRemoteParticipants,
  useRoomContext,
  useSessionContext,
  useTracks,
  useTrackToggle,
} from "@livekit/components-react";
import type { TrackReference } from "@livekit/components-core";
import { ConnectionState, ParticipantKind, RoomEvent, Track } from "livekit-client";

import { resetChat } from "./newChat";
import {
  MAX_REJOINS_PER_SESSION,
  isAgentLeftOnly,
  isRejoinCancelled,
  runRejoin,
  type TranscriptTurn,
} from "./agentRejoin";

// Published by the agent on "lk.call" when it leaves an idle chat (src/idle.py).
export const IDLE_END_EVENT = "idle_end";

/** What an agent signal on "lk.call" says. call_ending = the call is over (review
 *  screen); idle_end = the agent left a quiet chat (thread stays, no review). */
export function parseCallSignal(payload: Uint8Array): "call_ending" | "idle_end" | null {
  try {
    const event = JSON.parse(new TextDecoder().decode(payload))?.event;
    return event === "call_ending" || event === IDLE_END_EVENT ? event : null;
  } catch {
    return null; // other apps could share the channel
  }
}

export type AgentPresenceOutcome = "ignore" | "cleanup-now" | "wait" | "end-now" | "rejoin";

// True only for connection states that are a transient (re)connection in
// progress. NOT true for a settled Connected, and — critically — NOT true for a
// terminal Disconnected: a hard drop (LiveKit gave up reconnecting) must still
// end the chat and surface the ended state, never hang on an active-looking dead
// session. So we only tolerate the absence while actively (re)connecting.
export function isTransientConnectionState(state: ConnectionState): boolean {
  return (
    state === ConnectionState.Connecting ||
    state === ConnectionState.Reconnecting ||
    state === ConnectionState.SignalReconnecting
  );
}

// Pure decision for the participant-presence effect below.
//   ignore       → agent present, session not started, or agent never joined
//                  yet (don't end before it first connects).
//   cleanup-now  → a graceful end was already signaled (call_ending) and the
//                  agent has now left — just disconnect.
//   wait         → agent absent while the room is mid-(re)connect: a transient
//                  transport drop (e.g. the WebView's WebRTC briefly dropping
//                  when a native screen is pushed over it on Android). Do nothing
//                  and let LiveKit reconnect; the effect re-runs when the
//                  connection state settles. No arbitrary timeout.
//   rejoin       → agent absent from a fully Connected room with no end signaled
//                  (worker crash, deploy drain, network blip on the agent's
//                  side) and a rejoin is possible: ask for a fresh agent in the
//                  same room instead of ending the chat.
//   (idle)       → the agent left quietly after a long idle (idle_end): ignored,
//                  like the pre-join case, until the user's next send wakes it.
//   end-now      → agent absent and the room is NOT reconnecting — either fully
//                  Connected with no rejoin available, or Disconnected (a hard
//                  drop). End the conversation.
export function classifyAgentPresence({
  agentJoined,
  sessionStarted,
  agentEverJoined,
  conversationEnded,
  roomReconnecting,
  canRejoin = false,
  agentIdledOut = false,
}: {
  agentJoined: boolean;
  sessionStarted: boolean;
  agentEverJoined: boolean;
  conversationEnded: boolean;
  roomReconnecting: boolean;
  // Room is Connected and the caller can request a new agent (and hasn't used
  // up its rejoin budget). Never true for a Disconnected room.
  canRejoin?: boolean;
  // The agent left on purpose after a long idle (the idle_end signal). It is
  // not a drop: no rejoin and no ended state until the user next acts.
  agentIdledOut?: boolean;
}): AgentPresenceOutcome {
  if (agentJoined || !sessionStarted || !agentEverJoined) return "ignore";
  if (agentIdledOut && !conversationEnded) return "ignore";
  if (conversationEnded) return "cleanup-now";
  if (roomReconnecting) return "wait";
  return canRejoin ? "rejoin" : "end-now";
}

// What the user asked for from the ended screen — the shell remounts the
// session and opens the fresh one with it.
export type NextConversation = { message?: string; voice?: boolean };

export function useCallMachine({
  sessionStarted,
  ensureSession,
  endSession,
  onConversationEnded,
  requestAgentRejoin,
  getTranscript,
}: {
  sessionStarted: boolean;
  ensureSession: (withMic: boolean) => Promise<void>;
  endSession: () => Promise<void>;
  onConversationEnded: (next?: NextConversation) => void;
  // Ask the widget server for a new agent in this room (see /api/rejoin).
  // Omitted → an unexpected agent leave ends the chat as before.
  requestAgentRejoin?: (transcript: TranscriptTurn[], voice: boolean) => Promise<void>;
  getTranscript?: () => TranscriptTurn[];
}) {
  const session = useSessionContext();
  const agent = useAgent();
  // Subscribes to the actual mic track publication so external mute events
  // (OS revoke, headset hardware mute) keep the UI in sync.
  const micToggle = useTrackToggle({ source: Track.Source.Microphone });
  const room = useRoomContext();
  // The agent logs this room's name as its conversation_id. Latest non-empty
  // value, kept past disconnect so the ended screen and feedback can cite it.
  const conversationIdRef = useRef("");
  if (room.name) conversationIdRef.current = room.name;
  // Live room connection state. A transient transport drop (e.g. the WebView's
  // WebRTC briefly dropping when a native screen is pushed over it on Android)
  // moves this away from `Connected`, which we use to tell a transient
  // agent-absence apart from a genuine agent-leave (see the presence effect).
  const connectionState = useConnectionState(room);
  const remoteParticipants = useRemoteParticipants();
  // The agent proper. A video-avatar worker is also an agent participant, but
  // it publishes on the agent's behalf and can't answer the user on its own.
  const mainAgent = remoteParticipants.find(
    (p) => p.isAgent && !p.attributes?.["lk.publish_on_behalf"],
  );
  const agentJoined = !!mainAgent;
  const agentIdentityRef = useRef<string | null>(null);
  useEffect(() => {
    if (mainAgent) agentIdentityRef.current = mainAgent.identity;
  }, [mainAgent]);

  const sendAgentRpc = useCallback(
    async (method: string) => {
      try {
        const agent = Array.from(room.remoteParticipants.values()).find(
          (p) => p.isAgent && !p.attributes?.["lk.publish_on_behalf"],
        );
        if (agent) {
          await room.localParticipant.performRpc({
            destinationIdentity: agent.identity,
            method,
            payload: "",
          });
        }
      } catch {
        // best-effort
      }
    },
    [room],
  );

  const videoTracks = useTracks([Track.Source.Camera], { onlySubscribed: true });
  const avatarVideoTrack = (videoTracks.find(
    (t) =>
      t.participant.kind === ParticipantKind.AGENT &&
      t.participant.attributes?.["lk.publish_on_behalf"] &&
      t.publication,
  ) as TrackReference | undefined) ?? null;

  // The SDK's useAgent() latches "failed" and never recovers: its "agent left"
  // reason is only cleared by a full room disconnect, and its 30s connect timer
  // keeps running after the first agent connected (then trips on the latched
  // state, marking a healthy replacement "did not complete initializing"). So a
  // failure is only taken at face value while the agent has never been up.
  // Once it has connected, or when the only complaint is that it left, the
  // failure is stale: presence drives rejoin below, and the agent's state and
  // mic track come from the participant itself.
  const [, bumpAgentAttrs] = useState(0);
  useEffect(() => {
    const onAttrs = () => bumpAgentAttrs((n) => n + 1);
    room.on(RoomEvent.ParticipantAttributesChanged, onAttrs);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, onAttrs);
    };
  }, [room]);
  const liveAgentState = mainAgent?.attributes?.["lk.agent.state"] as AgentState | undefined;
  const isLive = (s: string | undefined) =>
    s === "listening" || s === "thinking" || s === "speaking";
  const agentEverConnectedRef = useRef(false);
  if (isLive(agent.state) || isLive(liveAgentState)) agentEverConnectedRef.current = true;
  const staleFailure =
    agent.state === "failed" &&
    (agentEverConnectedRef.current || isAgentLeftOnly(agent.failureReasons));
  const agentMicTracks = useTracks([Track.Source.Microphone], { onlySubscribed: true });
  let agentState: AgentState = agent.state;
  let microphoneTrack = agent.microphoneTrack;
  if (staleFailure) {
    agentState = mainAgent ? (liveAgentState ?? "initializing") : "connecting";
    microphoneTrack = agentMicTracks.find(
      (t) => t.participant.identity === mainAgent?.identity,
    ) as TrackReference | undefined;
  }

  // Optimistic "user wants voice mode" flag — set synchronously on click so
  // the UI flips to active (red end-call) immediately, without waiting for
  // the mic track publication to propagate through useTrackToggle. Merged
  // into inVoiceMode below; the SDK signal still wins for external mute
  // events (OS revoke, hardware), and we reset this on call end / failure.
  const [voiceIntent, setVoiceIntent] = useState(false);
  const inVoiceMode = micToggle.enabled || voiceIntent;
  // Sticky "this session ever used voice" flag. Used to gate the audio
  // renderer + visualizer after the call ends: we want Courtney's "talk soon"
  // goodbye to finish playing in voice mode, but in *text-only* mode the
  // user never opted into audio — without this flag, the data-channel
  // call_ending signal would mount RoomAudioRenderer for the first time
  // and play her goodbye audio uninvited.
  const [voiceModeUsed, setVoiceModeUsed] = useState(false);
  useEffect(() => {
    if (inVoiceMode) setVoiceModeUsed(true);
  }, [inVoiceMode]);
  // callPending: only meaningful when NOT yet in voice mode. Once mic is
  // published (or we're optimistically pretending it is), transient
  // agent.isPending blips don't disable the End Call button mid-call.
  // agent.isPending is deliberately NOT part of it: the chat connects on open
  // (before anyone asked for voice), and the phone button must not show a
  // "calling" spinner for that. Tapping it mid-connect is fine (see below).
  const callPending = !inVoiceMode && micToggle.pending;
  const [callError, setCallError] = useState<string | null>(null);
  // Set once the conversation has ended, recording who ended it. The
  // transcript stays on screen with the feedback prompt under it; the composer
  // stays too and starts a fresh session (startNewChat).
  const [endedBy, setEndedBy] = useState<"user" | "agent" | null>(null);
  const conversationEnded = endedBy !== null;
  // The agent left quietly after a long idle (idle_end). Not an ended
  // conversation (no review screen) and not a drop (no auto-rejoin): the thread
  // stays, and the user's next send/tap/mic wakes it (wakeFromIdle).
  const [idleEnded, setIdleEnded] = useState(false);

  // Unexpected agent drop → auto-rejoin (see the presence effect). `reconnecting`
  // drives the inline "Reconnecting to Courtney…" status; the refs carry the
  // loop's view of the world so it never needs to restart on a re-render.
  const [reconnecting, setReconnecting] = useState(false);
  const rejoinRef = useRef({ running: false, count: 0 });
  const live = useRef({
    agentJoined,
    conversationEnded,
    sessionStarted,
    inVoiceMode,
    userEnded: false,
    idleEnded: false,
    unmounted: false,
  });
  live.current.agentJoined = agentJoined;
  live.current.conversationEnded = conversationEnded;
  live.current.sessionStarted = sessionStarted;
  live.current.inVoiceMode = inVoiceMode;
  live.current.idleEnded = idleEnded;
  useEffect(() => {
    live.current.unmounted = false;
    return () => {
      live.current.unmounted = true;
    };
  }, []);

  // Mid-call media device errors (mic unplugged, OS revoked permission,
  // headset disconnected). The room emits these throughout the session, not
  // just at startup — the SDK's canonical surface for media failures.
  useEffect(() => {
    const onMediaDeviceError = (err: Error) => {
      console.error("[lk-widget] media device error:", err);
      setCallError(`Mic error: ${err.message || err.name || "unknown"}`);
    };
    session.room.on(RoomEvent.MediaDevicesError, onMediaDeviceError);
    return () => {
      session.room.off(RoomEvent.MediaDevicesError, onMediaDeviceError);
    };
  }, [session.room]);

  // Clear the optimistic voice-intent flag whenever the call has truly ended.
  // 'conversationEnded' covers the graceful path (user-end OR agent-disconnected
  // end); 'failed' covers the SDK timeout / agent error path.
  useEffect(() => {
    if (conversationEnded || agent.state === "failed") setVoiceIntent(false);
  }, [conversationEnded, agent.state]);

  // Track whether the agent ever joined this session. Without this, the
  // disconnect branch below would fire on the *initial* render (no agent
  // participant yet) and end the conversation as soon as the user clicks
  // Call. We watch participant presence directly because that signal lands
  // *before* agent.state finishes transitioning to 'disconnected'.
  const agentEverJoinedRef = useRef(false);
  useEffect(() => {
    if (agentJoined) agentEverJoinedRef.current = true;
  }, [agentJoined]);

  // Listen for the agent's "call_ending" data-channel signal. The Python
  // agent publishes this on the "lk.call" topic the moment _hangup starts,
  // BEFORE awaiting the goodbye audio playout. We mark the conversation ended
  // immediately while the goodbye ("...talk soon!") finishes streaming, so end
  // *feels* instant without cutting off Courtney's farewell. The transcript
  // stays on screen.
  // endSession() is intentionally NOT called here — the participant-left
  // effect below handles cleanup once the agent fully disconnects.
  useDataChannel("lk.call", (msg) => {
    if (conversationEnded || !sessionStarted) return;
    const signal = parseCallSignal(msg.payload);
    if (signal === IDLE_END_EVENT) {
      setIdleEnded(true);
    } else if (signal === "call_ending") {
      setVoiceIntent(false);
      setEndedBy("agent");
    }
  });

  // React to terminal agent states.
  //   'failed': SDK's agent-connect timeout (or other agent error) tripped —
  //             surface the SDK's failure reason + offer Retry. The SDK's
  //             "Agent left the room unexpectedly." reason is NOT handled here:
  //             it is sticky for the life of the room, so the presence logic
  //             below decides what an agent leave means.
  //   participant-left (mid-session, after agent had joined): either the agent
  //             ended the call (call_ending already marked it ended) or it
  //             dropped (crash, deploy drain). A drop with the room still
  //             connected rejoins: a fresh agent is dispatched into the same
  //             room with the transcript, and the thread stays on screen.
  //             We watch participant *presence* rather than agent.state
  //             because participant-left fires several frames sooner.
  // endSession() disconnects the room — mic publication ends → micToggle.enabled
  // flips false automatically. setVoiceIntent(false) is inlined so all UI
  // teardown lands in the same React commit (same trick as user-end).
  useEffect(() => {
    if (agent.state === "failed" && !staleFailure && !conversationEnded) {
      const reason = agent.failureReasons?.[0] ?? "Agent didn't connect";
      setCallError(`${reason}. Tap retry to try again.`);
      setVoiceIntent(false);
      endSession().catch(() => {});
      return;
    }
    const outcome = classifyAgentPresence({
      agentJoined,
      sessionStarted,
      agentEverJoined: agentEverJoinedRef.current,
      conversationEnded,
      roomReconnecting: isTransientConnectionState(connectionState),
      agentIdledOut: idleEnded,
      canRejoin:
        !!requestAgentRejoin &&
        connectionState === ConnectionState.Connected &&
        rejoinRef.current.count < MAX_REJOINS_PER_SESSION,
    });

    // "ignore" (agent present / pre-join) and "wait" (agent absent but the room
    // is mid-(re)connect — a transient drop, so let LiveKit reconnect) both mean
    // do nothing. We re-run when connectionState or agentJoined changes, so a
    // genuine leave is caught the moment the room settles back to Connected with
    // the agent still gone — no arbitrary timeout.
    if (outcome === "ignore" || outcome === "wait") return;

    if (outcome === "cleanup-now") {
      // The "call_ending" signal already marked the conversation ended and the
      // agent has now left — just clean up the session.
      endSession().catch(() => {});
      return;
    }

    if (outcome === "rejoin") {
      // One loop at a time: never two dispatches racing for the same room.
      if (rejoinRef.current.running || !requestAgentRejoin) return;
      rejoinRef.current.running = true;
      setReconnecting(true);
      const voice = live.current.inVoiceMode;
      void runRejoin({
        agentPresent: () => live.current.agentJoined,
        // Stop the moment the user ends/leaves or the session is torn down.
        cancelled: () =>
          isRejoinCancelled(live.current, room.state === ConnectionState.Disconnected),
        dispatch: () => requestAgentRejoin(getTranscript?.() ?? [], voice),
      }).then((result) => {
        rejoinRef.current.running = false;
        if (live.current.unmounted) return;
        setReconnecting(false);
        if (result === "joined") {
          rejoinRef.current.count += 1;
        } else if (result === "failed") {
          // Out of attempts: fall back to the error banner with Retry.
          setVoiceIntent(false);
          setCallError("Courtney dropped out of the chat. Tap retry to try again.");
          endSession().catch(() => {});
        }
      });
      return;
    }

    // "end-now": agent gone while the room is fully connected and we can't
    // rejoin (no handler, or too many drops this session), or the room itself
    // is Disconnected (a hard drop). Mark it ended so the user isn't stranded.
    setVoiceIntent(false);
    setEndedBy("agent");
    endSession().catch(() => {});
  }, [
    agent.state,
    agent.failureReasons,
    staleFailure,
    agentJoined,
    conversationEnded,
    sessionStarted,
    connectionState,
    reconnecting,
    idleEnded,
    session.room,
    endSession,
  ]);

  // The user acted after the agent left for idle: bring it back through the
  // normal rejoin path (the presence effect sees an absent agent and, with the
  // idle flag cleared, rejoins with the transcript). Fresh rejoin budget: idle
  // can come round many times in one long chat.
  const wakeFromIdle = () => {
    if (!idleEnded) return;
    rejoinRef.current.count = 0;
    setIdleEnded(false);
  };

  const startVoiceCall = async () => {
    if (inVoiceMode || callPending) return;
    wakeFromIdle();
    setCallError(null);
    // Flip the UI to voice-mode immediately. SDK signals will catch up over
    // the next few frames; on error we roll this back below.
    setVoiceIntent(true);
    try {
      // Synchronously kick the audio context inside the user gesture.
      session.room.startAudio().catch(() => {});
      if (!sessionStarted) {
        // Cold start: session.start opens the room AND publishes the mic.
        await ensureSession(true);
      } else {
        // Warm: a chat session is already live (or still connecting: it starts
        // on open). Wait for the room, then publish the mic via SDK toggle.
        if (session.connectionState !== ConnectionState.Connected) {
          await session.waitUntilConnected(AbortSignal.timeout(20_000));
        }
        await micToggle.toggle(true);
      }
      sendAgentRpc("enableTranscriptSync");
    } catch (err) {
      setVoiceIntent(false);
      console.error("[lk-widget] startVoiceCall failed:", err);
      const name = err instanceof Error ? err.name : "";
      const msg = err instanceof Error ? err.message : String(err);
      setCallError(
        name === "NotAllowedError"
          ? "Mic access needed for voice — you can keep chatting."
          : `Voice call failed: ${msg || name || "unknown error"}`,
      );
    }
  };

  const endConversation = async () => {
    live.current.userEnded = true;
    sendAgentRpc("disableTranscriptSync");
    setVoiceIntent(false);
    setEndedBy("user");
    try {
      // endSession disconnects the room — micToggle.enabled flips to false
      // automatically when the publication ends. The transcript stays on
      // screen since we don't remount the session here.
      await endSession();
    } catch (err) {
      console.error("[lk-widget] endConversation failed:", err);
    }
  };

  // The toolbar phone button: starts voice when idle, ends the whole
  // conversation (leaving the transcript on screen) when on a call.
  const toggleVoice = () => {
    if (inVoiceMode) void endConversation();
    else void startVoiceCall();
  };

  const startNewChat = (next?: NextConversation) => {
    if (!conversationEnded) return;
    // Bump the parent's epoch so AgentSession remounts with a fresh useSession.
    // This also clears the transcript (useSessionMessages is room-scoped), so
    // the user gets a clean slate. Without it, the next call would reuse the
    // (now-dead) cached room/token.
    onConversationEnded(next);
  };

  // Header "New chat": drop this conversation and open a fresh one now. Unlike
  // endConversation it never shows the ended/review screen, and unlike an agent
  // drop it must not trigger the auto-rejoin (userEnded cancels that loop).
  const startNewChatNow = () =>
    resetChat({
      markUserEnded: () => {
        live.current.userEnded = true;
        setVoiceIntent(false);
      },
      endSession,
      startFresh: () => onConversationEnded(),
    });

  const retryAfterError = () => {
    setCallError(null);
    // Bump the parent's epoch — forces AgentSession to remount with a fresh
    // useSession; without this the next start() would reuse the dead session
    // we just ended.
    onConversationEnded();
  };

  const showVisualizer =
    inVoiceMode || (voiceModeUsed && agentJoined && conversationEnded);

  return {
    state: {
      inVoiceMode,
      callPending,
      agentJoined,
      showVisualizer,
      agentState,
      microphoneTrack,
      reconnecting,
      avatarVideoTrack,
      agentIdentity: agentIdentityRef.current,
      conversationEnded,
      idleEnded,
      endedBy,
      conversationId: conversationIdRef.current,
      voiceModeUsed,
      callError,
    },
    actions: {
      startVoiceCall,
      toggleVoice,
      endConversation,
      startNewChat,
      wakeFromIdle,
      startNewChatNow,
      retryAfterError,
      setError: setCallError,
    },
  };
}
